import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { BRAND_LOGO_SRC } from '@/config/brand';
import { t, type MessageKey } from '@/i18n';

interface FooterLink {
  to?: string;
  href?: string;
  label: MessageKey;
}

interface FooterColumn {
  heading: MessageKey;
  links: FooterLink[];
}

const COLUMNS: FooterColumn[] = [
  {
    heading: 'landing.nav.product',
    links: [
      { to: '/channels', label: 'landing.nav.p.instagram' },
      { to: '/channels', label: 'landing.nav.p.whatsapp' },
      { to: '/channels', label: 'landing.nav.p.messenger' },
      { to: '/channels', label: 'landing.nav.p.email' },
      { to: '/flows', label: 'landing.nav.p.booking' },
    ],
  },
  {
    heading: 'landing.footer.col.resources',
    links: [
      { to: '/getting-started', label: 'landing.footer.gettingStarted' },
      { to: '/playbook', label: 'landing.pb.eyebrow' },
      { to: '/faq', label: 'landing.nav.faq' },
    ],
  },
  {
    heading: 'landing.footer.col.legal',
    links: [
      { to: '/privacy', label: 'landing.footer.privacy' },
      { to: '/terms', label: 'landing.footer.terms' },
      { to: '/cookies', label: 'landing.footer.cookies' },
      { to: '/acceptable-use', label: 'landing.footer.acceptableUse' },
      { to: '/accessibility-statement', label: 'landing.footer.accessibility' },
    ],
  },
  {
    heading: 'landing.footer.col.support',
    links: [
      { href: 'mailto:support@buzzbitx.com', label: 'landing.footer.contact' },
    ],
  },
];

/**
 * logo.png is 1024² with lockup ink ~901×436 on an opaque white canvas —
 * crop empty padding so the wordmark reads large. Sit on a white plate so
 * the dark ink stays legible on the dark footer (CSS invert would turn the
 * opaque canvas into a solid white box).
 */
function FooterLogo(): JSX.Element {
  return (
    <span className="relative block h-14 w-[128px] shrink-0 overflow-hidden rounded-xl bg-white sm:h-16 sm:w-[148px]">
      <img
        src={BRAND_LOGO_SRC}
        alt={t('brand.logoAlt')}
        width={148}
        height={64}
        className="pointer-events-none absolute left-1/2 top-[45%] h-[220%] w-[220%] max-w-none -translate-x-1/2 -translate-y-1/2 object-contain"
        loading="eager"
        decoding="async"
        draggable={false}
      />
    </span>
  );
}

function ColumnLink({ link }: { link: FooterLink }): JSX.Element {
  const className =
    'text-sm text-gray-400 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400/60 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-950 rounded-sm';
  const label = t(link.label);
  if (link.href) {
    return (
      <a href={link.href} className={className}>
        {label}
      </a>
    );
  }
  return (
    <Link to={link.to ?? '/'} className={className}>
      {label}
    </Link>
  );
}

/** Footer — shared by landing, playbook, legal and guide pages. */
export function Footer(): JSX.Element {
  return (
    <footer className="bg-gray-950 text-gray-400">
      <div className="mx-auto max-w-7xl px-4 pt-14 pb-10 md:px-6 md:pt-16 md:pb-12">
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-10">
          <div className="flex flex-col gap-5 lg:col-span-4">
            <Link to="/" className="inline-flex w-fit items-center" aria-label={t('landing.nav.home')}>
              <FooterLogo />
            </Link>
            <p className="max-w-xs text-sm leading-relaxed text-gray-500">
              {t('landing.footer.builtIn')}
            </p>
            <Link
              to="/signup"
              className="inline-flex w-fit items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-gray-900 transition-colors hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400/60 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-950"
            >
              {t('landing.nav.getStarted')}
              <ArrowRight className="size-4" aria-hidden="true" />
            </Link>
          </div>

          <nav
            aria-label={t('landing.footer.navLabel')}
            className="grid grid-cols-2 gap-8 sm:grid-cols-4 lg:col-span-8"
          >
            {COLUMNS.map((column) => (
              <div key={column.heading} className="flex flex-col gap-3.5">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-white">
                  {t(column.heading)}
                </h3>
                <ul className="flex flex-col gap-2.5">
                  {column.links.map((link) => (
                    <li key={`${link.label}-${link.to ?? link.href}`}>
                      <ColumnLink link={link} />
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>
        </div>

        {/* Israeli trader-disclosure line — legally required content, kept
            verbatim and visible (English, like the rest of the site — QA T8.4). */}
        <div className="mt-12 flex flex-col gap-3 border-t border-white/10 pt-8 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
          <p className="text-xs text-gray-500">{t('landing.footer.rights')}</p>
          <p className="text-xs text-gray-500 sm:text-right">{t('landing.legal.line')}</p>
        </div>
      </div>
    </footer>
  );
}
